import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Mail, ArrowLeft, CheckCircle } from 'lucide-react';
import { authApi } from '../api/auth';

export default function ForgotPasswordPage() {
  const { t, i18n } = useTranslation();
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const [isLangMenuOpen, setIsLangMenuOpen] = useState(false);

  const changeLanguage = (lng: string) => {
    i18n.changeLanguage(lng);
    setIsLangMenuOpen(false);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      await authApi.forgotPassword({ email, lang: i18n.language });
      setSuccess(true);
    } catch (err: any) {
      if (err.response?.status === 429) {
        setError(t('forgotPassword.tooManyRequests'));
      } else {
        setError(err.response?.data?.message || t('forgotPassword.error'));
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-start sm:items-center justify-center bg-gradient-to-br from-dark-950 via-dark-900 to-dark-800 px-4 pt-16 sm:pt-0 pb-8">
      {/* Language Switcher */}
      <div className="fixed top-4 right-4 z-50">
        <div className="relative">
          <button
            onClick={() => setIsLangMenuOpen(!isLangMenuOpen)}
            className="px-4 py-2 text-sm font-medium text-white bg-gradient-to-r from-primary-600/80 to-secondary-600/80 hover:from-primary-500 hover:to-secondary-500 rounded-lg transition-all flex items-center space-x-2 shadow-lg"
          >
            <span className="text-lg">🌐</span>
            <span className="uppercase font-bold">{i18n.language}</span>
            <span className={`text-xs transition-transform ${isLangMenuOpen ? 'rotate-180' : ''}`}>▼</span>
          </button>
          {isLangMenuOpen && (
            <div className="absolute right-0 mt-2 w-40 bg-dark-900 border border-white/20 rounded-lg shadow-2xl z-50 animate-scale-in">
              {[
                { code: 'es', name: '🇪🇸 Español' },
                { code: 'en', name: '🇬🇧 English' },
                { code: 'ru', name: '🇷🇺 Русский' },
                { code: 'lt', name: '🇱🇹 Lietuvių' },
              ].map((lang) => (
                <button
                  key={lang.code}
                  onClick={() => changeLanguage(lang.code)}
                  className={`w-full px-4 py-3 text-left text-sm hover:bg-white/10 first:rounded-t-lg last:rounded-b-lg transition-all ${
                    i18n.language === lang.code
                      ? 'bg-gradient-to-r from-primary-600/30 to-secondary-600/30 text-white font-semibold'
                      : 'text-gray-300'
                  }`}
                >
                  {lang.name}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="max-w-md w-full">
        <div className="text-center mb-6 animate-fade-in">
          <Link to="/" className="inline-flex items-center justify-center mb-3 hover:opacity-80 transition-opacity">
            <img src="/landing/logo-light.svg" alt="Fonorex" className="h-16" />
          </Link>
          <p className="text-gray-400 text-base sm:text-lg px-4">{t('forgotPassword.subtitle')}</p>
        </div>

        <div className="backdrop-blur-xl bg-white/10 border border-white/20 rounded-3xl shadow-2xl p-8 animate-scale-in">
          {success ? (
            <div className="text-center">
              <div className="w-20 h-20 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
                <CheckCircle className="w-10 h-10 text-green-400" />
              </div>
              <h2 className="text-2xl font-bold text-white mb-2">
                {t('forgotPassword.successTitle')}
              </h2>
              <p className="text-gray-400 mb-6">
                {t('forgotPassword.successDescription')}
              </p>
              <Link
                to="/login"
                className="inline-flex items-center gap-2 text-primary-400 hover:text-primary-300 font-semibold transition-colors"
              >
                <ArrowLeft className="w-4 h-4" />
                {t('forgotPassword.backToLogin')}
              </Link>
            </div>
          ) : (
            <>
              <div className="text-center mb-6">
                <div className="w-16 h-16 bg-gradient-to-r from-primary-600/20 to-secondary-600/20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Mail className="w-8 h-8 text-primary-400" />
                </div>
                <h2 className="text-xl font-bold text-white">
                  {t('forgotPassword.title')}
                </h2>
              </div>

              {error && (
                <div className="mb-6 p-4 bg-red-500/20 border border-red-500/50 text-red-200 rounded-xl backdrop-blur-sm">
                  {error}
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <label htmlFor="email" className="block text-sm font-semibold text-gray-300 mb-2">
                    {t('forgotPassword.email')}
                  </label>
                  <input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    autoComplete="email"
                    className="w-full px-4 py-3 bg-white/5 border border-white/10 text-white rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none transition-all placeholder-gray-500"
                    placeholder={t('forgotPassword.emailPlaceholder')}
                    required
                  />
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-gradient-to-r from-primary-600 to-secondary-600 hover:from-primary-500 hover:to-secondary-500 text-white font-bold py-4 px-4 rounded-xl transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-lg hover:shadow-glow"
                >
                  {loading ? t('forgotPassword.sending') : t('forgotPassword.sendButton')}
                </button>
              </form>

              <p className="mt-8 text-center text-sm text-gray-400">
                <Link
                  to="/login"
                  className="inline-flex items-center gap-1 text-primary-400 hover:text-primary-300 font-semibold transition-colors"
                >
                  <ArrowLeft className="w-4 h-4" />
                  {t('forgotPassword.backToLogin')}
                </Link>
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
